import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Anchor, Container } from 'components/base';
import {
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  WIDTH_LADDERS,
  getCloudinarySrcSet,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';

import Breadcrumb from '../components/breadcrumb';
import {
  Picture,
  PriceTag,
  Responsive,
  landingImage,
  productImage,
} from '../components/landing/shared';
import { Head, Layout } from '../components/layout';
import PdpIcon from '../components/product/pdpIcon';
import ProductFaq from '../components/product/productFaq';
import { productPrice } from '../lib/landingPrice';
import tracker from '../lib/tracker';
import SETTINGS from '../settings';

// Baby Photo Books landing page (docs/baby-photo-books.md). One story page that sells the photo
// book: first days, then the first year. Copy follows the Pencil frames "Baby Photo Books —
// Redesign" / "— Mobile (iPhone)". The CTAs change per device, the indexed content does not.
const CAMPAIGN = 'baby_photo_books';

const HREF = {
  // Main web path until a direct editor entry is confirmed.
  WEB: '/photo-books',
  HARDCOVER: '/hardcover-book',
  SOFTCOVER: '/softcover-books',
  PREMIUM: '/premium-hardcover-books',
  SQUARE_PRINTS: '/square-prints',
  GIFT_CARD: '/giftcard',
  ENGINEER_PRINT: '/engineer-prints',
  RAILS: '/wooden-rails',
};

const APP_STORE_HREF = (() => {
  const url = new URL(SETTINGS.PHOTO_BOOKS_APP_STORE_URL);
  url.searchParams.set('utm_source', 'website');
  url.searchParams.set('utm_medium', 'landing');
  url.searchParams.set('utm_campaign', CAMPAIGN);
  return url.href;
})();

const PRODUCT_ID = {
  HARDCOVER: 'hardcover-book',
  SOFTCOVER: 'softcover-books',
};

const IMAGES = {
  hero: landingImage('sc-mom-baby'),
  withYou: productImage('classic-prints/3'),
  // Archive image, must be on the CDN before release (design/images/archive/MANIFEST.md).
  final: { imageKey: 'landing/baby-photo-books/hc-mom-baby-open', widths: WIDTH_LADDERS.landing },
  firstDays: productImage('softcover-books/6'),
  hardcover: productImage('hardcover-book/1'),
  softcover: landingImage('sc-captions-open'),
  premium: landingImage('pr-leather-foil'),
  firstYear: productImage('softcover-books/3'),
  squarePrints: productImage('square-prints/1'),
  engineerPrints: { imageKey: 'instagram/instagram1', widths: WIDTH_LADDERS.instagram },
};
// The square photo covers a slightly tall box, so it renders as wide as the box is high.
const HERO_SIZES = '(min-width: 992px) 560px, 80vw';

const APP_SCREENS = {
  gallery: '/static/img/app-book-gallery.webp',
  layout: '/static/img/app-book-layout.webp',
};

const BREADCRUMB = [
  { name: 'Home', url: '/' },
  { name: 'Photo Books', url: '/photo-books' },
  { name: 'Baby Photo Books' },
];

// `intent` tells first-days and first-year interest apart, `path` the web or App Store route.
const track =
  ({ location, intent = 'general', product = 'photo_book', path = 'web' }) =>
  () => {
    tracker.onCustomEvent(`${CAMPAIGN}_cta_click`, { location, intent, product, path });
  };

// iPhone and iPad (iPadOS reports a Mac with touch). Everyone else gets the web path as the
// primary phone CTA. Rendered as iPhone on the server, so the badge is in the first paint there.
const isAppleMobile = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const useWebOnly = () => {
  const [webOnly, setWebOnly] = useState(false);
  useEffect(() => setWebOnly(!isAppleMobile()), []);
  return webOnly;
};

const ArrowButton = ({ href, children, onClick, className }) => (
  <Link href={href} className={classNames('lnd-btn', className)} onClick={onClick}>
    {children}
    <PdpIcon name="arrowRight" size={18} />
  </Link>
);

ArrowButton.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

const TextLink = ({ href, children, onClick, className }) => (
  <Link href={href} className={classNames('bpb-link', className)} onClick={onClick}>
    {children}
    <PdpIcon name="arrowRight" size={16} />
  </Link>
);

TextLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

const AppStoreBadge = ({ location, className }) => (
  <Anchor
    href={APP_STORE_HREF}
    className={classNames('bpb-badge', className)}
    targetBlank
    onClick={track({ location, path: 'app_store' })}
  >
    <img
      src="/static/img/app-store-badge.svg"
      alt="Download Parabo Press on the App Store"
      width="162"
      height="54"
    />
  </Anchor>
);

AppStoreBadge.propTypes = {
  location: PropTypes.string.isRequired,
  className: PropTypes.string,
};

// Desktop handoff to the phone. The code opens the same App Store page as the badge, and the
// tile links there too for keyboard and screen reader users.
const QrHandoff = ({ location, children, className }) => (
  <Anchor
    href={APP_STORE_HREF}
    className={classNames('bpb-qr', className)}
    targetBlank
    onClick={track({ location, path: 'app_store' })}
  >
    <span className="bpb-qr-code">
      <img
        src="/static/img/photo-books-app-qr.svg"
        alt="QR code for the Parabo Press app on the App Store"
        width="68"
        height="68"
      />
    </span>
    <span className="bpb-qr-label">{children}</span>
  </Anchor>
);

QrHandoff.propTypes = {
  location: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// Hero and final CTA. Desktop: web button + QR. iPhone: App Store badge + web link. Other
// phones: the web button only.
const BookCta = ({ location }) => (
  <div className="bpb-cta">
    <ArrowButton href={HREF.WEB} className="bpb-cta-web" onClick={track({ location })}>
      Make your baby photo book
    </ArrowButton>
    <QrHandoff location={location} className="bpb-cta-desk">
      Get the iPhone app.
      <br />
      Scan with your iPhone
    </QrHandoff>
    <AppStoreBadge location={location} className="bpb-cta-app" />
    <TextLink href={HREF.WEB} className="bpb-cta-app" onClick={track({ location })}>
      Create on the web
    </TextLink>
  </div>
);

BookCta.propTypes = {
  location: PropTypes.string.isRequired,
};

const PhoneFrame = ({ src, alt, className, priority }) => (
  <div className={classNames('bpb-phone', className)}>
    <img src={src} alt={alt} width="480" height="1043" loading={priority ? undefined : 'lazy'} />
  </div>
);

PhoneFrame.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  priority: PropTypes.bool,
};

const IconList = ({ items, iconSize, className }) => (
  <ul className={className}>
    {items.map(({ icon, label }) => (
      <li key={label}>
        <PdpIcon name={icon} size={iconSize} />
        {label}
      </li>
    ))}
  </ul>
);

IconList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ icon: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  iconSize: PropTypes.number,
  className: PropTypes.string,
};

const HERO_FACTS = [
  { icon: 'smartphone', label: 'iPhone app or web' },
  { icon: 'book', label: 'Softcover or Hardcover' },
  { icon: 'mapPin', label: 'Printed in the USA' },
];

const PROMPTS = [
  { icon: 'send', label: 'The ones you sent to family' },
  { icon: 'repeat', label: 'The ones you keep opening again' },
  { icon: 'sun', label: 'A few ordinary days' },
  { icon: 'user', label: 'One with you in it' },
];

const FIRST_DAYS_OUTLINE = [
  'The first cuddle',
  'Coming home',
  'The people who came close',
  'Ordinary days so far',
];

const APP_STEPS = [
  {
    src: APP_SCREENS.gallery,
    alt: 'Parabo Press iPhone app showing the photos chosen for a photo book',
    title: 'See the photos you chose',
    phoneTitle: 'Your photos',
    text: 'Every photo for your book in one place before it goes on the page.',
    phoneText: 'Every photo for your book in one place.',
  },
  {
    src: APP_SCREENS.layout,
    alt: 'Parabo Press iPhone app showing the Change Layout panel for a Softcover Photo Book page',
    title: 'Arrange the pages',
    phoneTitle: 'Arrange pages',
    text: 'Change a page layout, or touch and hold a page to drag it into order.',
    phoneText: 'Change a layout or drag pages into order.',
  },
];

const BOOK_SPECS = [
  { icon: 'ruler', label: '8x8 in square' },
  { icon: 'book', label: '26 pages included, up to 100' },
  { icon: 'image', label: '1 to 3 photos per page' },
];

const FIRST_YEAR_OUTLINE = [
  ['The beginning', 'Their first days, from the moments you already have.'],
  ['The months in between', 'A few favorite moments, not every month or milestone.'],
  ['The people who were there', 'Grandparents, friends, siblings, and you.'],
  ['Where this chapter ends', 'Their first birthday, or whenever it feels right to you.'],
];

const PRINTS_MATH = [
  { value: 5, label: 'favorite photos', phone: 'photos' },
  { value: 4, label: 'copies of each', phone: 'copies' },
  { value: 20, label: 'Square Prints, one set', phone: 'one set' },
];

const ENGINEER_FACTS = [
  { icon: 'ruler', label: '36 × 48 in, one photo full size' },
  { icon: 'layers', label: 'Extra-light paper with a white 1/2 in border' },
  { icon: 'frame', label: 'Hang it with Wood Poster Rails, sold separately' },
];

// Shape ProductFaq renders. `description` is HTML, the FAQPage JSON-LD gets it without tags.
const FAQ = [
  {
    caption: 'Can I make a baby photo book before their first birthday?',
    description:
      'Yes. Start with their first days, first weeks, or the moments you’ve shared so far. The book can cover the chapter you choose.',
  },
  {
    caption: 'Can I make my book from my iPhone?',
    description:
      'You can make Softcover and Hardcover Photo Books in the Parabo Press iPhone app. You can also create a book on the web. Premium Photo Books use a separate computer or tablet workflow with QR upload from your phone.',
  },
  {
    caption: 'How do I choose which baby photos to include?',
    description:
      'Start with the photos you keep coming back to or have already shared with family, then find the originals on your phone. Add a few everyday moments and the people who belong in the story.',
  },
  {
    caption: 'How many photos can I include?',
    description:
      'Softcover and Hardcover Photo Books have 26 to 100 pages. The number of photos depends on the layouts you choose, including layouts with more than one photo on a page.',
  },
  {
    caption: 'Are the photos printed on the pages?',
    description: 'Yes. Your chosen photos are printed directly onto the pages of your photo book.',
  },
  {
    caption: 'When will my book arrive?',
    description:
      'Delivery time depends on your book, shipping method and destination. Production times, transit times and holiday order-by dates are on our <a href="/shipping">Shipping page</a>.',
  },
];

const FAQ_JSON_LD = FAQ.map(({ caption, description }) => ({
  question: caption,
  answer: description.replace(/<[^>]+>/g, ''),
}));

const Hero = () => (
  <section className="lnd-hero bpb-hero">
    <Container className="bpb-hero-inner">
      <div className="bpb-hero-copy">
        <Breadcrumb items={BREADCRUMB} />
        <h1 className="lnd-h1 bpb-h1">Baby photo books for their first days and first year</h1>
        <p className="lnd-lead bpb-lead">
          The photos you keep sending to family. The little moments you keep coming back to. Bring
          them together in a book, starting with the pictures already on your phone.
        </p>
        <p className="bpb-printed">
          <PdpIcon name="book" size={18} />
          Your photos, printed right onto the pages.
        </p>
        <BookCta location="hero" />
        <IconList items={HERO_FACTS} iconSize={16} className="bpb-hero-facts lnd-desk" />
      </div>

      <div className="bpb-scene">
        <figure className="bpb-scene-book">
          <Picture
            image={IMAGES.hero}
            sizes={HERO_SIZES}
            alt="A parent holds a sleeping baby and looks through an open 8x8 Softcover Photo Book"
            priority
          />
          <figcaption className="bpb-pill">
            <Responsive desktop="8x8 Softcover Photo Book" phone="8x8 Softcover" />
          </figcaption>
        </figure>
        <PhoneFrame
          src={APP_SCREENS.gallery}
          alt="Book photos in the Parabo Press iPhone app"
          className="bpb-scene-phone"
          priority
        />
        <p className="bpb-scene-label">
          <PdpIcon name="arrowLeft" size={14} />
          Your book photos in the Parabo Press app
        </p>
      </div>
    </Container>
  </section>
);

const Favorites = () => (
  <section className="bpb-section">
    <Container className="bpb-split bpb-favorites">
      <figure className="bpb-photo bpb-favorites-photo">
        <Picture
          image={IMAGES.withYou}
          sizes="(min-width: 992px) 540px, calc(100vw - 40px)"
          alt="A hand holds out two Classic Prints of a mom and her baby"
        />
      </figure>
      <div className="bpb-copy">
        <p className="bpb-eyebrow">Where to start</p>
        <h2 className="bpb-h2">Start with the photos you already love</h2>
        <p className="bpb-body">
          Think of the pictures you’ve already sent to someone you love. Find the originals on your
          phone and start there: a tiny hand, a sleepy cuddle, an ordinary afternoon together. Add
          the moments that feel like your family.
        </p>
        <IconList items={PROMPTS} iconSize={18} className="bpb-prompts" />
        <p className="bpb-yourself">Include yourself, too. You’re part of this story.</p>
        <TextLink href={HREF.WEB} onClick={track({ location: 'favorites' })}>
          Start with your favorites
        </TextLink>
      </div>
    </Container>
  </section>
);

const FirstDays = () => (
  <section className="bpb-section bpb-cream">
    <Container className="bpb-split bpb-first-days">
      <div className="bpb-copy">
        <p className="bpb-eyebrow">Newborn photo book</p>
        <h2 className="bpb-h2">Their first days are a chapter of their own</h2>
        <p className="bpb-body">
          A book can begin with the days you’ve shared so far. The first cuddle you photographed.
          The place you came home to. The people who came close. Choose the parts of the story you
          want to hold onto.
        </p>
        <p className="bpb-reassure">
          You can make a newborn photo book before their first birthday.
        </p>
        <ArrowButton
          href={HREF.WEB}
          className="bpb-btn-block"
          onClick={track({ location: 'first_days', intent: 'first_days' })}
        >
          Make a book of their first days
        </ArrowButton>
      </div>
      <div className="bpb-first-days-example">
        <figure className="bpb-photo bpb-first-days-photo">
          <Picture
            image={IMAGES.firstDays}
            sizes="(min-width: 992px) 560px, calc(100vw - 40px)"
            alt="An open Softcover Photo Book with baby photos"
          />
        </figure>
        <div>
          <h3 className="bpb-h3">One way to shape a first-days book</h3>
          <ol className="bpb-outline">
            {FIRST_DAYS_OUTLINE.map((step, index) => (
              <li key={step}>
                <span className="bpb-outline-num">{String(index + 1).padStart(2, '0')}</span>
                {step}
              </li>
            ))}
          </ol>
          <p className="bpb-note">
            An example to borrow, not a template. Softcover and Hardcover books start at 26 pages
            and grow to 100.
          </p>
        </div>
      </div>
    </Container>
  </section>
);

const IPhoneApp = () => (
  <section className="bpb-section bpb-mint">
    <Container className="bpb-split bpb-app">
      <div className="bpb-copy bpb-app-copy">
        <p className="bpb-eyebrow">From your phone to the page</p>
        <h2 className="bpb-h2">Make room for a photo book, right on your iPhone</h2>
        <p className="bpb-body">
          Your photos are already on your phone. Use the Parabo Press iPhone app to choose the ones
          you want in a Softcover or Hardcover Photo Book and bring them together on the page.
        </p>
        <div className="bpb-app-actions">
          <QrHandoff location="app" className="bpb-cta-desk">
            Scan with
            <br />
            your iPhone
          </QrHandoff>
          <AppStoreBadge location="app" className="bpb-cta-app" />
          <TextLink href={HREF.WEB} onClick={track({ location: 'app' })}>
            Create on the web
          </TextLink>
        </div>
      </div>
      <ol className="bpb-app-screens">
        {APP_STEPS.map((step, index) => (
          <li key={step.title}>
            <PhoneFrame src={step.src} alt={step.alt} />
            <h3 className="bpb-app-step">
              <span className="bpb-app-num">{index + 1}</span>
              <Responsive desktop={step.title} phone={step.phoneTitle} />
            </h3>
            <p className="bpb-app-text">
              <Responsive desktop={step.text} phone={step.phoneText} />
            </p>
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

const PagesNote = () => (
  <p className="bpb-pages-note">
    <PdpIcon name="layoutGrid" size={18} />
    <Responsive
      desktop="Pages are not photos. Each page holds 1 to 3 photos, so the number of photos depends on the layouts you choose."
      phone="Pages are not photos. Each page holds 1 to 3 photos, so the number of photos depends on your layouts."
    />
  </p>
);

const ChooseBook = () => {
  const dataContext = useDataContext();
  const { previewSummary } = useClaimedCoupon();
  const hardcover = productPrice(dataContext.getProduct(PRODUCT_ID.HARDCOVER), previewSummary);
  const softcover = productPrice(dataContext.getProduct(PRODUCT_ID.SOFTCOVER), previewSummary);

  return (
    <section className="bpb-section">
      <Container>
        <div className="bpb-books-head">
          <div>
            <p className="bpb-eyebrow">Choose your book</p>
            <h2 className="bpb-h2">Choose a book for this chapter</h2>
          </div>
          <span className="lnd-desk">
            <PagesNote />
          </span>
        </div>

        <div className="bpb-books">
          <article className="bpb-book bpb-book--lead">
            <figure className="bpb-book-photo">
              <Picture
                image={IMAGES.hardcover}
                sizes="(min-width: 992px) 340px, calc(100vw - 40px)"
                alt="Hands holding an open 8x8 Hardcover Photo Book"
              />
            </figure>
            <div className="bpb-book-content">
              <div className="bpb-book-top">
                <p className="bpb-tag">Our pick for a baby book</p>
                <p className="bpb-book-product">
                  8x8 Hardcover Photo Book
                  <span className="lnd-phone">
                    {' · '}
                    <PriceTag price={{ ...hardcover, priceFrom: false }} />
                  </span>
                </p>
                <h3 className="bpb-book-title">A hardcover home for their story</h3>
                <p className="bpb-book-text">
                  Bring their first days or first year together in a Hardcover Photo Book to keep on
                  your shelf and open together.
                </p>
                <IconList items={BOOK_SPECS} iconSize={16} className="bpb-book-specs" />
              </div>
              <div className="bpb-book-bottom">
                <p className="bpb-price lnd-desk">
                  <span className="bpb-price-label">26 pages</span>
                  <PriceTag
                    price={{ ...hardcover, priceFrom: false }}
                    className="bpb-price-value"
                  />
                </p>
                <ArrowButton
                  href={HREF.HARDCOVER}
                  className="bpb-btn-block"
                  onClick={track({ location: 'choose_book', product: PRODUCT_ID.HARDCOVER })}
                >
                  Make a Hardcover Photo Book
                </ArrowButton>
              </div>
            </div>
          </article>

          <article className="bpb-book bpb-book--soft">
            <figure className="bpb-book-photo">
              <Picture
                image={IMAGES.softcover}
                sizes="(min-width: 992px) 400px, (min-width: 768px) 100vw, 96px"
                alt="An open Softcover Photo Book with captions under the photos"
              />
            </figure>
            <div className="bpb-book-content">
              <div className="bpb-book-top">
                <p className="bpb-book-product">
                  <Responsive
                    desktop="Softcover Photo Book · 6x6 or 8x8 in"
                    phone={
                      <>
                        Softcover · 6x6 or 8x8 · <PriceTag price={softcover} />
                      </>
                    }
                  />
                </p>
                <h3 className="bpb-book-title">A softcover for the next little chapter</h3>
                <p className="bpb-book-text lnd-desk">
                  Choose a Softcover Photo Book for their first weeks, a family visit, or another
                  chapter you want to collect.
                </p>
              </div>
              <div className="bpb-book-bottom">
                <p className="bpb-price lnd-desk">
                  {/* Any size: with a size-restricted coupon the cheapest one can be 8x8. */}
                  <span className="bpb-price-label">From</span>
                  <PriceTag
                    price={{ ...softcover, priceFrom: false }}
                    className="bpb-price-value"
                  />
                </p>
                <TextLink
                  href={HREF.SOFTCOVER}
                  onClick={track({ location: 'choose_book', product: PRODUCT_ID.SOFTCOVER })}
                >
                  Explore Softcover
                </TextLink>
              </div>
            </div>
          </article>
        </div>

        <span className="lnd-phone">
          <PagesNote />
        </span>

        <div className="bpb-premium">
          <figure className="bpb-premium-thumb lnd-desk">
            <Picture image={IMAGES.premium} sizes="96px" alt="" />
          </figure>
          <div className="bpb-premium-copy">
            <h3 className="bpb-premium-title">Looking for a larger book project?</h3>
            <p>
              Explore Premium Photo Books on your computer or tablet, with QR upload for photos from
              your phone. Not available in the iPhone app.
            </p>
          </div>
          <TextLink
            href={HREF.PREMIUM}
            onClick={track({ location: 'choose_book', product: 'premium-hardcover-books' })}
          >
            Explore Premium Photo Books
          </TextLink>
        </div>
      </Container>
    </section>
  );
};

const FirstYear = () => (
  <section className="bpb-section bpb-cream">
    <Container className="bpb-split bpb-first-year">
      <figure className="bpb-first-year-figure">
        <div className="bpb-photo bpb-first-year-photo">
          <Picture
            image={IMAGES.firstYear}
            sizes="(min-width: 992px) 500px, calc(100vw - 40px)"
            alt="A 6x6 Softcover Photo Book titled Our Very First Year with a family photo on the cover"
          />
        </div>
        <figcaption className="bpb-caption lnd-desk">
          A 6x6 Softcover Photo Book titled Our Very First Year
        </figcaption>
      </figure>
      <div className="bpb-copy">
        <p className="bpb-eyebrow">Baby’s first year photo book</p>
        <h2 className="bpb-h2">Bring their first year together</h2>
        <p className="bpb-body">
          When you’re ready for a baby’s first year photo book, let a few favorite moments lead the
          way. A face that changed. A place you went back to. The people who were there. Make room
          for the everyday days, too.
        </p>
        {/* Open by default, the outline is part of the indexed content. */}
        <details className="bpb-year-outline" open>
          <summary>
            A first-year outline to borrow
            <PdpIcon name="chevronDown" size={18} />
          </summary>
          <dl>
            {FIRST_YEAR_OUTLINE.map(([step, text]) => (
              <div key={step}>
                <dt>{step}</dt>
                <dd>{text}</dd>
              </div>
            ))}
          </dl>
        </details>
        <p className="bpb-inspiration lnd-desk">
          Start with the beginning, add the moments you love from the months in between, and finish
          wherever this chapter ends for you.
        </p>
        <ArrowButton
          href={HREF.WEB}
          className="bpb-btn-block"
          onClick={track({ location: 'first_year', intent: 'first_year' })}
        >
          Make their first year photo book
        </ArrowButton>
      </div>
    </Container>
  </section>
);

const SquarePrints = () => (
  <section className="bpb-section">
    <Container className="bpb-split bpb-prints">
      <div className="bpb-copy">
        <p className="bpb-eyebrow">Baby photo prints</p>
        <h2 className="bpb-h2">A few favorites to share</h2>
        <p className="bpb-body">
          Print a few of the photos you love for the people who love them, too. Square Prints make a
          small collection to keep nearby or give to family.
        </p>
        <p className="bpb-math">
          <span className="sr-only">
            5 favorite photos times 4 copies of each equals 20 Square Prints, one set
          </span>
          {PRINTS_MATH.map((item, index) => (
            <React.Fragment key={item.value}>
              {index > 0 && (
                <span className="bpb-math-op" aria-hidden="true">
                  {index === 1 ? '×' : '='}
                </span>
              )}
              <span className="bpb-math-item" aria-hidden="true">
                <span className="bpb-math-value">{item.value}</span>
                <span className="bpb-math-label">
                  <Responsive desktop={item.label} phone={item.phone} />
                </span>
              </span>
            </React.Fragment>
          ))}
        </p>
        <p className="bpb-example">
          One set of 20 Square Prints becomes four little collections of five to share. You sort
          them into groups yourself.
        </p>
        <TextLink
          href={HREF.SQUARE_PRINTS}
          onClick={track({ location: 'square_prints', product: 'square-prints' })}
        >
          Make Square Prints
        </TextLink>
        <div className="bpb-gift">
          <p>
            Making a gift? Use photos the parents are happy to share. A Parabo Press gift card lets
            them choose their own.
          </p>
          <TextLink
            href={HREF.GIFT_CARD}
            onClick={track({ location: 'square_prints', product: 'giftcard' })}
          >
            Give a gift card
          </TextLink>
        </div>
      </div>
      <figure className="bpb-photo bpb-prints-photo">
        <Picture
          image={IMAGES.squarePrints}
          sizes="(min-width: 992px) 540px, calc(100vw - 40px)"
          alt="Square Prints of children and family photos spread on a table"
        />
      </figure>
    </Container>
  </section>
);

const EngineerPrint = () => (
  <section className="bpb-section bpb-cream">
    <Container className="bpb-split bpb-engineer">
      <figure className="bpb-engineer-figure">
        <div className="bpb-photo bpb-engineer-photo">
          <Picture
            image={IMAGES.engineerPrints}
            sizes="(min-width: 992px) 480px, calc(100vw - 40px)"
            alt=""
          />
        </div>
        <figcaption className="bpb-caption">
          Two black and white Engineer Prints of baby photos in a playroom
        </figcaption>
      </figure>
      <div className="bpb-copy">
        <p className="bpb-eyebrow">One photo, big</p>
        <h2 className="bpb-h2">Their first photo, big enough for the wall</h2>
        <p className="bpb-body">
          Some photos deserve more than a page. Print the first photo of you together, or the one
          everyone asks about, as a 3x4 foot Engineer Print.
        </p>
        <IconList items={ENGINEER_FACTS} iconSize={16} className="bpb-facts" />
        <p className="bpb-tip">
          Pick a photo with one clear subject and use the original file from your phone, not a
          screenshot, so it prints sharp.
        </p>
        <div className="bpb-links">
          <TextLink
            href={HREF.ENGINEER_PRINT}
            onClick={track({ location: 'engineer_print', product: 'engineer-prints' })}
          >
            Make an Engineer Print
          </TextLink>
          <TextLink
            href={HREF.RAILS}
            onClick={track({ location: 'engineer_print', product: 'wooden-rails' })}
          >
            See Wood Poster Rails
          </TextLink>
        </div>
      </div>
    </Container>
  </section>
);

// Shared PDP FAQ pattern, first question open.
const Faq = () => (
  <section className="bpb-section bpb-faq">
    <Container>
      <ProductFaq
        eyebrow="Before you order"
        title="Questions about your baby photo book"
        intro={false}
        items={FAQ}
      />
    </Container>
  </section>
);

const FinalCta = () => (
  <section className="bpb-section bpb-cream bpb-final">
    <Container className="bpb-split bpb-final-inner">
      <div className="bpb-copy">
        <h2 className="bpb-h2 bpb-h2--large">Start with the moments you have</h2>
        <p className="bpb-final-body">
          Their first days can become a book now. The next chapter can come when you’re ready.
        </p>
        <BookCta location="final" />
      </div>
      <figure className="bpb-photo bpb-final-photo">
        <Picture
          image={IMAGES.final}
          sizes="(min-width: 992px) 560px, calc(100vw - 40px)"
          alt="A parent holds a sleeping baby and an open 8x8 Hardcover Photo Book"
        />
      </figure>
    </Container>
  </section>
);

const BabyPhotoBooksPage = () => {
  const webOnly = useWebOnly();

  return (
    <>
      <Head
        title="Baby Photo Books | Newborn & First Year"
        description="Turn the baby photos you love into a printed photo book. Start with their first days or bring a whole year together, on your iPhone or on the web."
        image={getCloudinaryUrl({ imageKey: IMAGES.hero.imageKey, format: 'jpg' })}
        heroImage={getCloudinaryUrl({ imageKey: IMAGES.hero.imageKey })}
        heroImageSrcSet={getCloudinarySrcSet(IMAGES.hero)}
        heroImageSizes={HERO_SIZES}
      />
      <ScriptMarkupBreadcrumb items={BREADCRUMB} host={SETTINGS.HOST} />
      <ScriptMarkupFaq items={FAQ_JSON_LD} />

      <Layout>
        <div className={classNames('lnd bpb', { 'bpb--web-only': webOnly })}>
          <Hero />
          <Favorites />
          <FirstDays />
          <IPhoneApp />
          <ChooseBook />
          <FirstYear />
          <SquarePrints />
          <EngineerPrint />
          <Faq />
          <FinalCta />
        </div>
      </Layout>
    </>
  );
};

export default BabyPhotoBooksPage;
